import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import type { CarrierDto } from '@/types/materials';

function buildSchema(t: (key: string) => string) {
  return z.object({
    code: z.string().min(1, t('form.validation.codeRequired')),
    name: z.string().min(1, t('form.validation.nameRequired')),
    status: z.enum(['Active', 'Inactive']),
  });
}

export type CarrierFormValues = z.infer<ReturnType<typeof buildSchema>>;

const DEFAULTS: CarrierFormValues = { code: '', name: '', status: 'Active' };

interface CarrierFormDialogProps {
  open: boolean;
  carrier: CarrierDto | null;
  onClose: () => void;
  onSubmit: (values: CarrierFormValues) => void;
  loading?: boolean;
}

// New carrier has no Status field on the request (CreateCarrierRequest is Code/Name only — every
// new row starts Active server-side, same as most masters) — the Status dropdown here only ever
// matters, and only ever gets sent, when editing an existing one (UpdateCarrierRequest).
export default function CarrierFormDialog({ open, carrier, onClose, onSubmit, loading }: CarrierFormDialogProps) {
  const { t } = useTranslation('carrierMaster');
  const schema = buildSchema(t);

  const { control, handleSubmit, reset } = useForm<CarrierFormValues>({
    resolver: zodResolver(schema),
    defaultValues: DEFAULTS,
  });

  useEffect(() => {
    if (open) {
      reset(carrier ? { code: carrier.code, name: carrier.name, status: carrier.status as 'Active' | 'Inactive' } : DEFAULTS);
    }
  }, [open, carrier, reset]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{carrier ? carrier.name : t('addCarrier')}</DialogTitle>
      <DialogContent>
        {/* MUI removes DialogContent's own top padding whenever it immediately follows a
            DialogTitle (to avoid double-spacing between them) — without this extra top padding,
            the very first field's shrunk outlined label (which floats slightly above its own
            border) has no clearance from the dialog's top edge and gets visually clipped. */}
        <Box sx={{ pt: 1 }}>
          <GridContainer spacing={2} columns={1}>
            <GridItem xs={1}>
              <Controller
                name="code"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField {...field} label={t('form.code')} fullWidth error={Boolean(fieldState.error)} helperText={fieldState.error?.message} />
                )}
              />
            </GridItem>
          <GridItem xs={1}>
            <Controller
              name="name"
              control={control}
              render={({ field, fieldState }) => (
                <TextField {...field} label={t('form.name')} fullWidth error={Boolean(fieldState.error)} helperText={fieldState.error?.message} />
              )}
            />
          </GridItem>
          {carrier && (
            <GridItem xs={1}>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <TextField {...field} select label={t('form.status')} fullWidth>
                    <MenuItem value="Active">{t('form.statusActive')}</MenuItem>
                    <MenuItem value="Inactive">{t('form.statusInactive')}</MenuItem>
                  </TextField>
                )}
              />
            </GridItem>
          )}
          </GridContainer>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t('discardChanges')}</Button>
        <Button variant="contained" disabled={loading} onClick={handleSubmit(onSubmit)}>
          {t('saveCarrier')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
